import React, { useState, useEffect } from 'react';
import { RefreshCw, LayoutDashboard, Users, ClipboardList } from 'lucide-react';
import Button from '../ui/Button';
import OverallHoursStats from './OverallHoursStats';
import StudentHoursChart from './StudentHoursChart';
import AttendanceRecords from './AttendanceRecords';
import { attendanceService, studentService, courseService, advancedReportService } from '../../services';

const AttendanceModuleView = ({ searchTrigger = 0, onRecordClick, onTokenAccess }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 内部Tab相关状态：'overview', 'students', 'attendance'
  const [activeTab, setActiveTab] = useState('overview');
  
  // 第一层数据 (概览)
  const [totalRemainingHours, setTotalRemainingHours] = useState(0);
  const [remainingHoursStudentCount, setRemainingHoursStudentCount] = useState(0);
  const [weeklyHoursData, setWeeklyHoursData] = useState([]);
  const [growthRate, setGrowthRate] = useState(0);
  const [lowHoursStudents, setLowHoursStudents] = useState([]);
  
  // 第二层数据 (学生)
  const [studentHoursData, setStudentHoursData] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc');
  
  // 第三层数据 (考勤 -> 明细)
  const [selectedStudent, setSelectedStudent] = useState('all');
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendancePage, setAttendancePage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [students, setStudents] = useState([]);
  const [studentCourses, setStudentCourses] = useState([]);
  
  // 弹出层和搜索相关状态
  const [showStudentSelector, setShowStudentSelector] = useState(false);
  const [showCourseSelector, setShowCourseSelector] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const sentinelRef = React.useRef(null); // 滚动加载哨兵

  // 加载主数据
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [summary, studentsData, coursesData, lowHoursData, rankingData] = await Promise.all([
        advancedReportService.getReportSummary({ months: 12 }).catch(() => ({ summary: {} })),
        studentService.getList({ page: 1, per_page: 1000, sort_by: 'recent_attendance', sort_order: 'desc' }).catch(() => ({ items: [] })),
        courseService.getNameList().catch(() => []),
        advancedReportService.getLowHoursStudents({ threshold: 2 }).catch(() => ({ low_hours_students: [] })),
        advancedReportService.getStudentHoursRanking({ order: sortOrder, limit: 1000 }).catch(() => ({ students_data: [] }))
      ]);
      
      const summaryData = summary.summary || {};
      setTotalRemainingHours(summaryData.total_remaining_hours || 0);
      setRemainingHoursStudentCount(summaryData.remaining_hours_student_count || 0);
      setLowHoursStudents(lowHoursData.low_hours_students || []);
      
      // 排序月度数据
      const sortedMonthlyData = (summaryData.monthly_consumption_data || []).sort((a, b) => {
        const parseMonth = (monthStr) => {
          if (monthStr.includes('-')) {
            const parts = monthStr.split('-');
            return { year: parseInt(parts[0]), month: parseInt(parts[1]) };
          }
          if (monthStr.includes('年')) {
            const match = monthStr.match(/(\d{4})年(\d{1,2})月/);
            if (match) return { year: parseInt(match[1]), month: parseInt(match[2]) };
          }
          if (monthStr.includes('月')) {
            const match = monthStr.match(/(\d{1,2})月/);
            if (match) return { year: new Date().getFullYear(), month: parseInt(match[1]) };
          }
          return { year: new Date().getFullYear(), month: 1 };
        };
        const monthA = parseMonth(a.month);
        const monthB = parseMonth(b.month);
        return monthA.year !== monthB.year ? monthB.year - monthA.year : monthB.month - monthA.month;
      });
      
      setWeeklyHoursData(sortedMonthlyData);
      setGrowthRate(summaryData.growth_rate || 0);
      // 过滤掉课时为0的学生，只展示“有课时”的学生
      const filteredRanking = (rankingData.students_data || []).filter(s => (s.remaining_hours || 0) > 0);
      setStudentHoursData(filteredRanking); 
      setStudents(studentsData.items || []);
    } catch (err) {
      setError(err.message || '加载失败');
      console.error('加载考勤模块数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 加载考勤记录
  // 接受显式的 studentId/courseId 参数，避免闭包捕获过期的 state导致滞后
  const loadAttendanceRecords = async (pageNum = 1, append = false, studentId = selectedStudent, courseId = selectedCourse) => {
    try {
      setLoadingMore(true);
      
      const params = { 
        page: pageNum,
        per_page: 20,
      };
      if (studentId && studentId !== 'all') params.student_id = studentId;
      if (courseId && courseId !== 'all') params.course_id = courseId;
      
      // 默认只显示正常记录
      if (!params.status) {
        params.status = 'ACTIVE';
      }
      
      const response = await advancedReportService.getAttendanceRecords(params);
      const records = response.items || response.records || [];
      
      if (append) {
        setAttendanceRecords(prev => [...prev, ...records]);
      } else {
        setAttendanceRecords(records);
      }
      
      setHasMore(response.has_next || false);
      setAttendancePage(pageNum);
    } catch (error) {
      console.error('加载考勤记录失败:', error);
      if (!append) {
        setAttendanceRecords([]);
      }
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadAttendanceRecords(attendancePage + 1, true, selectedStudent, selectedCourse);
    }
  };

  useEffect(() => {
    loadData();
  }, [searchTrigger]);

  useEffect(() => {
    // 直接将最新的 state 值传入，避免闭包问题
    loadAttendanceRecords(1, false, selectedStudent, selectedCourse);
  }, [selectedStudent, selectedCourse, searchTrigger]);

  // 使用 IntersectionObserver 监听滚动到底部 (仅在明细 Tab 激活时)
  useEffect(() => {
    if (activeTab !== 'attendance' || !sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !loadingMore && hasMore) {
          console.log('📥 考勤明细触发加载更多 (IntersectionObserver)');
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, [activeTab, hasMore, loadingMore, attendancePage, selectedStudent, selectedCourse]);

  useEffect(() => {
    const loadStudentCourses = async () => {
      if (selectedStudent && selectedStudent !== 'all') {
        try {
          const response = await advancedReportService.getStudentCourses(selectedStudent);
          setStudentCourses(response.courses || []);
        } catch (error) {
          console.error('加载学生课程失败:', error);
          setStudentCourses([]);
        }
      } else {
        setStudentCourses([]);
      }
    };
    loadStudentCourses();
  }, [selectedStudent]);

  return (
    <div className="flex flex-col space-y-3">
      {/* 内部 Tab 切换 (Segmented Pill Style) */}
      <div className="flex justify-center mb-1">
        <div className="bg-[#EDF2F7] p-1 rounded-xl flex w-full max-w-[340px] shadow-inner">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg text-xs font-bold transition-all ${
              activeTab === 'overview'
                ? 'bg-white text-[#3182CE] shadow-sm'
                : 'text-[#718096] hover:text-[#4A5568]'
            }`}
          >
            <LayoutDashboard size={14} />
            概览
          </button>
          <button
            onClick={() => setActiveTab('students')}
            className={`flex-1 flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg text-xs font-bold transition-all ${
              activeTab === 'students'
                ? 'bg-white text-[#3182CE] shadow-sm'
                : 'text-[#718096] hover:text-[#4A5568]'
            }`}
          >
            <Users size={14} />
            学生
          </button>
          <button
            onClick={() => setActiveTab('attendance')}
            className={`flex-1 flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg text-xs font-bold transition-all ${
              activeTab === 'attendance'
                ? 'bg-white text-[#3182CE] shadow-sm'
                : 'text-[#718096] hover:text-[#4A5568]'
            }`}
          >
            <ClipboardList size={14} />
            明细
          </button>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <RefreshCw size={32} className="text-[#3182CE] animate-spin mb-3" />
            <p className="text-sm text-[#718096]">加载中...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
            <p className="text-sm text-red-700 font-medium">加载失败</p>
            <button onClick={loadData} className="mt-2 text-xs text-red-700 underline">重试</button>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <OverallHoursStats
                totalRemainingHours={totalRemainingHours}
                remainingHoursStudentCount={remainingHoursStudentCount}
                weeklyHoursData={weeklyHoursData}
                growthRate={growthRate}
                lowHoursStudents={lowHoursStudents}
              />
            )}
            {activeTab === 'students' && (
              <StudentHoursChart
                studentHoursData={studentHoursData}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
              />
            )}
            {activeTab === 'attendance' && (
              <div className="pb-10">
                <AttendanceRecords
                  selectedStudent={selectedStudent}
                  setSelectedStudent={setSelectedStudent}
                  selectedCourse={selectedCourse}
                  setSelectedCourse={setSelectedCourse}
                  attendanceRecords={attendanceRecords}
                  students={students}
                  studentCourses={studentCourses}
                  showStudentSelector={showStudentSelector}
                  setShowStudentSelector={setShowStudentSelector}
                  showCourseSelector={showCourseSelector}
                  setShowCourseSelector={setShowCourseSelector}
                  studentSearchTerm={studentSearchTerm}
                  setStudentSearchTerm={setStudentSearchTerm}
                  courseSearchTerm={courseSearchTerm}
                  setCourseSearchTerm={setCourseSearchTerm}
                  handleLoadMore={handleLoadMore}
                  loadingMore={loadingMore}
                  hasMore={hasMore}
                  onRecordClick={onRecordClick}
                  onTokenAccess={onTokenAccess}
                />
                {/* 加载更多哨兵 */}
                <div ref={sentinelRef} className="h-4 w-full" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AttendanceModuleView;
